// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/lib/api.ts
// Rôle : appelle les capacités de l'API publique. Aucune route privée.
// Référence : CIG-ARCHITECTURE.md §3 ; CIG-SPECIFICATION.md §12.5.

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/** Capacités mutantes, appelées en `POST`. Les autres sont lues en `GET`. */
const MUTANTES = new Set<string>();

let catalogue: Promise<Map<string, { mutates: boolean; path: string; method: 'GET' | 'POST' }>> | null = null;

/**
 * Charge le registre des capacités. L'interface n'appelle que l'API publique,
 * et découvre ses routes depuis le registre lui-même : elle ne peut donc pas
 * diverger de la matrice de parité (spécification §12.5).
 */
async function routes(
  forcer = false,
): Promise<Map<string, { mutates: boolean; path: string; method: 'GET' | 'POST' }>> {
  if (forcer) catalogue = null;
  if (!catalogue) {
    catalogue = fetch('/api/v1/admin/capabilities', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new ApiError('internal', 'Registre indisponible.', r.status);
        const body = (await r.json()) as {
          capabilities: { id: string; mutates: boolean; route: { method: 'GET' | 'POST'; path: string } }[];
        };
        const m = new Map<string, { mutates: boolean; path: string; method: 'GET' | 'POST' }>();
        for (const c of body.capabilities) {
          m.set(c.id, { mutates: c.mutates, path: c.route.path, method: c.route.method });
          if (c.mutates) MUTANTES.add(c.id);
        }
        return m;
      })
      .catch((e) => {
        catalogue = null;
        throw e;
      });
  }
  return catalogue;
}

export interface CallOptions {
  /** Identifiant d'opération, pour l'idempotence des mutations. */
  opId?: string;
  /** Précondition de version, pour les mutations concurrentes. */
  ifMatch?: string;
}

/** Appelle une capacité. C'est le seul chemin d'accès de l'interface. */
/**
 * Résout la route d'une capacité.
 *
 * Le registre est mémorisé pour la durée de la page, mais une capacité absente
 * n'est pas tenue pour inexistante d'emblée : le serveur a pu être redéployé
 * depuis le chargement. Le registre est alors relu une fois. Sans cela, une
 * page ouverte avant une mise à jour resterait durablement infirme, sans autre
 * remède qu'un rechargement manuel dont l'utilisateur ignore la nécessité.
 */
async function routeDe(
  capabilityId: string,
): Promise<{ mutates: boolean; path: string; method: 'GET' | 'POST' }> {
  const connue = (await routes()).get(capabilityId);
  if (connue) return connue;

  const relue = (await routes(true)).get(capabilityId);
  if (relue) return relue;
  throw new ApiError('not_found', `Capacité inconnue : ${capabilityId}.`, 404);
}

export async function call<T = Record<string, unknown>>(
  capabilityId: string,
  input: Record<string, unknown> = {},
  opts: CallOptions = {},
): Promise<T> {
  const route = await routeDe(capabilityId);

  const headers: Record<string, string> = {};
  if (opts.opId) headers['idempotency-key'] = opts.opId;
  if (opts.ifMatch) headers['if-match'] = opts.ifMatch;

  let res: Response;
  if (route.method === 'GET') {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      q.set(k, typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v));
    }
    const suffixe = q.toString();
    res = await fetch(route.path + (suffixe ? `?${suffixe}` : ''), {
      credentials: 'same-origin',
      headers,
    });
  } else {
    res = await fetch(route.path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { ...headers, 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  if (res.status === 401) {
    // Une session expirée renvoie vers le fournisseur d'identité.
    window.location.href = '/auth/login';
    throw new ApiError('unauthenticated', 'Session expirée.', 401);
  }

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      String(body.error ?? 'internal'),
      String(body.message ?? 'Erreur inattendue.'),
      res.status,
      (body.detail as Record<string, unknown>) ?? {},
    );
  }
  return body as T;
}

/**
 * Appelle une capacité mutante en remontant la progression de l'envoi.
 *
 * `fetch` ne rend pas la progression d'émission : un envoi de plusieurs
 * mébioctets n'y produirait aucun retour. `XMLHttpRequest` l'expose, ce qui
 * permet d'afficher une progression réelle plutôt qu'une animation feinte.
 */
export function appelerAvecProgression<T = Record<string, unknown>>(
  chemin: string,
  input: Record<string, unknown>,
  opts: CallOptions & { onProgression?: (part: number) => void } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', chemin, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/json');
    if (opts.opId) xhr.setRequestHeader('idempotency-key', opts.opId);
    if (opts.ifMatch) xhr.setRequestHeader('if-match', opts.ifMatch);

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) opts.onProgression?.(ev.loaded / ev.total);
    };

    xhr.onload = () => {
      let corps: Record<string, unknown> = {};
      try {
        corps = JSON.parse(xhr.responseText) as Record<string, unknown>;
      } catch {
        corps = {};
      }
      if (xhr.status === 401) {
        window.location.href = '/auth/login';
        reject(new ApiError('unauthenticated', 'Session expirée.', 401));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(
          String(corps.error ?? 'internal'),
          String(corps.message ?? 'Erreur inattendue.'),
          xhr.status,
          (corps.detail as Record<string, unknown>) ?? {},
        ));
        return;
      }
      // La réponse est reçue ; l'envoi est nécessairement complet.
      opts.onProgression?.(1);
      resolve(corps as T);
    };

    xhr.onerror = () => reject(new ApiError('internal', 'Envoi interrompu.', 0));
    xhr.onabort = () => reject(new ApiError('internal', 'Envoi annulé.', 0));
    xhr.send(JSON.stringify(input));
  });
}

/** Résout le chemin REST d'une capacité, pour les appels directs. */
export async function cheminDe(capabilityId: string): Promise<string> {
  return (await routeDe(capabilityId)).path;
}

/** Identifiant d'opération, pour rendre une mutation idempotente. */
export function nouvelOpId(): string {
  return crypto.randomUUID();
}

/**
 * Ouvre le canal temps réel. Ce que voit l'utilisateur est l'état du serveur,
 * pas un instantané (spécification §1.5, §5.2.2).
 */
export function ouvrirCanal(onEvent: (e: CigEventClient) => void): () => void {
  let socket: WebSocket | null = null;
  let ferme = false;
  let delai = 500;

  const connecter = (): void => {
    if (ferme) return;
    const protocole = window.location.protocol === 'https:' ? 'wss' : 'ws';
    socket = new WebSocket(`${protocole}://${window.location.host}/ws`);
    socket.onopen = () => {
      delai = 500;
    };
    socket.onmessage = (m) => {
      try {
        onEvent(JSON.parse(String(m.data)) as CigEventClient);
      } catch {
        // Un message illisible est ignoré.
      }
    };
    socket.onclose = () => {
      if (ferme) return;
      // Reconnexion à intervalle croissant, plafonné : le canal ne doit pas
      // marteler le serveur après une coupure.
      setTimeout(connecter, delai);
      delai = Math.min(delai * 2, 30_000);
    };
  };
  connecter();

  return () => {
    ferme = true;
    socket?.close();
  };
}

export interface CigEventClient {
  type: string;
  nodeId: string | null;
  actorRef: string | null;
  sub?: string | null;
  at: string;
  data?: Record<string, unknown>;
}

/** Déclare la présence de l'utilisateur sur un dossier. */
export function declarerPresence(socketEnvoi: (m: string) => void, nodeId: string | null): void {
  socketEnvoi(JSON.stringify({ type: 'presence', nodeId }));
}

export function formaterTaille(octets: number): string {
  const unites = ['o', 'Kio', 'Mio', 'Gio', 'Tio'];
  let v = octets;
  let i = 0;
  while (v >= 1024 && i < unites.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${unites[i]}`;
}

export function formaterDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
